import type { Locales } from '@/types/project-types'

import { envNumber } from '@maestro/core/config/env'
import { createLogger } from '@maestro/core/logger'
import { theme } from '@maestro/local/theme'

export const MAESTRO_LANG_COOKIE = 'MAESTRO_LANG'

const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365
export const MAESTRO_LANG_COOKIE_MAX_AGE = envNumber(
  process.env.NEXT_PUBLIC_MAESTRO_LANG_COOKIE_MAX_AGE,
  ONE_YEAR_IN_SECONDS
)

const log = createLogger('maestro:i18n-request')

// Maps deprecated/macrolanguage codes to the canonical BCP 47 subtag we support.
const LANGUAGE_ALIASES: Record<string, string> = {
  no: 'nb',
}

function applyLanguageAlias(tag: string): string {
  const [language, ...rest] = tag.split('-')
  const alias = LANGUAGE_ALIASES[language]
  if (!alias) {
    return tag
  }
  return rest.length ? `${alias}-${rest.join('-')}` : alias
}

export function parseAcceptLanguage(
  acceptLanguageHeader: string | null
): string[] {
  if (!acceptLanguageHeader) {
    return []
  }

  return acceptLanguageHeader
    .split(',')
    .map((part) => {
      const [tag, qPart] = part.trim().split(';')
      const q = qPart?.startsWith('q=') ? parseFloat(qPart.slice(2)) : 1

      return { tag: tag.trim(), q: Number.isFinite(q) ? q : 0 }
    })
    .sort((a, b) => b.q - a.q)
    .map(({ tag }) => tag)
}

export function findSupportedLocale(tag: string | null): Locales | null {
  if (!tag) {
    return null
  }

  const supportedLocales = theme().site.supportedLocales
  const lowered = applyLanguageAlias(tag.toLowerCase())

  const matchedLocale = supportedLocales.find((l) => {
    return l.code.toLowerCase() === lowered
  })

  if (matchedLocale) {
    return matchedLocale.code
  }

  // if we will get only language without region, then try to find any supported locale with the same language
  const prefix = supportedLocales.find((l) => {
    return l.code.toLowerCase().split('-')[0] === lowered.split('-')[0]
  })

  return prefix?.code ?? null
}

export function getSupportedLocale(tag: string | null): Locales {
  const matched = findSupportedLocale(tag)
  if (matched) {
    return matched
  }

  const defaultLocale = theme().site.locale
  log.info(
    `[i18n-request] Provided locale is not supported. Selected default language: ${defaultLocale}.`
  )
  return defaultLocale
}

/**
 * Reduces a locale tag to the SAP OCC `lang` short code (e.g. `de-DE` -> `de`,
 * `nb-NO` -> `nb`).
 */
export function localeToSapLang(tag: string | null): string {
  return getSupportedLocale(tag).toLowerCase().split('-')[0]
}
